import { storyTime } from '../format';
import { supabase, unwrapFunctionError } from '../supabase';
import type { FeedItem, Profile, ReadingList, Source, Story } from '../types';
import {
  FEED_COLUMNS,
  QuotaError,
  VISIBLE,
  contentByIds,
  getCategories,
  invalidateSourceMap,
  recoSince,
  throwIfQuota,
  withDeks,
} from './content';

// ---------- Kişisel brifing (mobildeki fetchDigest ile aynı algoritma) ----------

const DIGEST_STEPS: readonly (readonly [number, number])[] = [
  [5, 4],
  [10, 6],
  [15, 8],
  [20, 10],
  [25, 12],
  [30, 15],
];
export const MAX_DIGEST_MIN = 30;

export function digestItemCount(budgetMin: number): number {
  const m = Math.max(0, Math.min(MAX_DIGEST_MIN, Math.round(budgetMin || 0)));
  const last = DIGEST_STEPS[DIGEST_STEPS.length - 1];
  if (m >= last[0]) return last[1];
  for (let i = 0; i < DIGEST_STEPS.length; i++) {
    const [stepMin, stepItems] = DIGEST_STEPS[i];
    if (m <= stepMin) {
      if (i === 0) return stepItems;
      const [prevMin, prevItems] = DIGEST_STEPS[i - 1];
      return Math.max(4, Math.round(prevItems + ((m - prevMin) / (stepMin - prevMin)) * (stepItems - prevItems)));
    }
  }
  return last[1];
}

const FAVORITE_BOOST = 5;
const TASTE_BOOST = 2.5;

export type Digest = { stories: Story[]; target: number; widened: boolean };

/**
 * Süre bütçesine göre kişisel paket. Bugün okunanlar pakette kalır (ilerleme
 * gösterilir); son 14 günde daha önce okunanlar elenir. Hat yavaşsa 48 saatlik
 * pencere 7 güne genişler, bu durum `widened` ile işaretlenir.
 */
export async function fetchDigest(userId: string, budgetMin: number, lang: string): Promise<Digest> {
  const target = digestItemCount(budgetMin);
  const [{ data: interests, error: ie }, favorites, readEarlier, muted] = await Promise.all([
    supabase.from('user_interests').select('subcategory_id,score').eq('user_id', userId).gte('score', 4),
    getFavoriteSourceIds(userId).catch(() => new Set<string>()),
    getReadIdsBefore(userId, startOfToday(), 14).catch(() => new Set<string>()),
    Promise.resolve(getMutedSourceIds(userId)),
  ]);
  if (ie) throw ie;
  if (!interests || interests.length === 0) return { stories: [], target, widened: false };

  const scoreBySub = new Map(interests.map((i) => [i.subcategory_id as string, Number(i.score)]));
  const candidateLimit = Math.max(24, Math.min(150, target * 6));

  const pull = async (since: string) => {
    const { data, error } = await supabase
      .from('content')
      .select(FEED_COLUMNS)
      .in('subcategory_id', [...scoreBySub.keys()])
      .or(VISIBLE)
      .gte('published_at', since)
      .lte('published_at', new Date(Date.now() + 3600_000).toISOString())
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(candidateLimit);
    if (error) throw error;
    return ((data ?? []) as FeedItem[]).filter((it) => !readEarlier.has(it.id) && !muted.has(it.source_id));
  };

  let fresh = await pull(recoSince());
  let widened = false;
  if (fresh.length < Math.min(target, 4)) {
    fresh = await pull(recoSince(24 * 7));
    widened = true;
  }

  let sims = new Map<string, number>();
  if (fresh.length > 0) {
    const { data } = await supabase.rpc('taste_similarity', { p_candidate_ids: fresh.map((f) => f.id) });
    sims = new Map(((data ?? []) as { content_id: string; similarity: number }[]).map((r) => [r.content_id, Number(r.similarity)]));
  }

  const byCat = new Map<string, { weight: number; items: FeedItem[] }>();
  for (const it of fresh) {
    const key = it.category_id ?? 'other';
    const w =
      (scoreBySub.get(it.subcategory_id ?? '') ?? 0) +
      (favorites.has(it.source_id) ? FAVORITE_BOOST : 0) +
      (sims.get(it.id) ?? 0) * TASTE_BOOST;
    const g = byCat.get(key) ?? { weight: 0, items: [] };
    g.weight = Math.max(g.weight, w);
    g.items.push(it);
    byCat.set(key, g);
  }
  for (const g of byCat.values()) {
    g.items = g.items
      .map((it, i) => ({ it, i, rank: (favorites.has(it.source_id) ? 1000 : 0) + (sims.get(it.id) ?? 0) * 100 }))
      .sort((a, b) => b.rank - a.rank || a.i - b.i)
      .map((x) => x.it);
  }
  const groups = [...byCat.values()].sort((a, b) => b.weight - a.weight);
  const take = groups.map(() => 0);
  let remaining = Math.min(target, fresh.length);
  while (remaining > 0) {
    let progressed = false;
    for (let i = 0; i < groups.length && remaining > 0; i++) {
      if (take[i] >= groups[i].items.length) continue;
      take[i] += 1;
      remaining -= 1;
      progressed = true;
    }
    if (!progressed) break;
  }
  const picked = groups.flatMap((g, i) => g.items.slice(0, take[i])).sort((a, b) => storyTime(b) - storyTime(a));
  return { stories: await withDeks(picked, lang), target, widened };
}

/** digest_days satırı — mobildeki "geçmiş brifingler" ile ortak. */
export async function logDigestDay(userId: string, stories: Story[]) {
  if (stories.length === 0) return;
  const minutes = Math.max(
    1,
    Math.round(stories.reduce((s, it) => s + (it.summary_word_count || 400), 0) / 180)
  );
  await supabase.from('digest_days').upsert(
    {
      user_id: userId,
      day: localDay(new Date()),
      item_count: stories.length,
      minutes,
      content_ids: stories.map((s) => s.id),
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,day' }
  );
}

export async function fetchPastDigests(userId: string) {
  const { data, error } = await supabase
    .from('digest_days')
    .select('day,item_count,minutes,content_ids')
    .eq('user_id', userId)
    .lt('day', localDay(new Date()))
    .order('day', { ascending: false })
    .limit(30);
  if (error) throw error;
  return (data ?? []) as { day: string; item_count: number; minutes: number; content_ids: string[] }[];
}

function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// ---------- Susturulan kaynaklar (mobilde cihazda; web'de tarayıcıda) ----------

export function getMutedSourceIds(userId: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(`muted-sources:${userId}`) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

// ---------- Okumalar ----------

export async function getReadIdsBefore(userId: string, before: Date, days: number): Promise<Set<string>> {
  const since = new Date(before.getTime() - days * 86400_000).toISOString();
  const { data } = await supabase
    .from('content_reads')
    .select('content_id')
    .eq('user_id', userId)
    .gte('read_at', since)
    .lt('read_at', before.toISOString())
    .limit(2000);
  return new Set((data ?? []).map((r) => r.content_id as string));
}

export async function getReadIdsToday(userId: string): Promise<Set<string>> {
  const { data } = await supabase
    .from('content_reads')
    .select('content_id')
    .eq('user_id', userId)
    .gte('read_at', startOfToday().toISOString());
  return new Set((data ?? []).map((r) => r.content_id as string));
}

/** Okuma kaydı açar; çıkışta finishRead ile süre yazılır. */
export async function startRead(userId: string, contentId: string): Promise<string | null> {
  const { data } = await supabase
    .from('content_reads')
    .insert({ user_id: userId, content_id: contentId, duration_sec: 0, cards_seen: 1 })
    .select('id')
    .single();
  return (data?.id as string | undefined) ?? null;
}

export async function finishRead(readId: string, durationSec: number, cardsSeen: number) {
  await supabase
    .from('content_reads')
    .update({ duration_sec: Math.min(3600, Math.round(durationSec)), cards_seen: cardsSeen })
    .eq('id', readId);
}

export type ReadStats = {
  streak: number;
  today: number;
  week: number;
  /** 7 gün × 6 dört saatlik dilim, en eski gün önce. */
  heat: number[][];
  days: { day: string; count: number }[];
};

export async function getReadStats(userId: string): Promise<ReadStats> {
  const since = new Date(Date.now() - 60 * 86400_000).toISOString();
  const { data } = await supabase
    .from('content_reads')
    .select('read_at')
    .eq('user_id', userId)
    .gte('read_at', since)
    .order('read_at', { ascending: false })
    .limit(5000);
  const times = (data ?? []).map((r) => new Date(r.read_at as string));
  const byDay = new Map<string, number>();
  for (const d of times) byDay.set(localDay(d), (byDay.get(localDay(d)) ?? 0) + 1);

  const today = new Date();
  let streak = 0;
  const cursor = new Date(today);
  if (!byDay.has(localDay(cursor))) cursor.setDate(cursor.getDate() - 1);
  while (byDay.has(localDay(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  const heat = Array.from({ length: 7 }, () => Array(6).fill(0) as number[]);
  const start = startOfToday();
  start.setDate(start.getDate() - 6);
  let week = 0;
  for (const d of times) {
    const dayIdx = Math.floor((d.getTime() - start.getTime()) / 86400_000);
    if (dayIdx < 0 || dayIdx > 6) continue;
    heat[dayIdx][Math.min(5, Math.floor(d.getHours() / 4))] += 1;
    week += 1;
  }
  const days: { day: string; count: number }[] = [];
  for (let i = 27; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push({ day: localDay(d), count: byDay.get(localDay(d)) ?? 0 });
  }
  return { streak, today: byDay.get(localDay(today)) ?? 0, week, heat, days };
}

export async function fetchHistory(userId: string, lang: string): Promise<Story[]> {
  const { data, error } = await supabase
    .from('content_reads')
    .select('content_id,read_at')
    .eq('user_id', userId)
    .order('read_at', { ascending: false })
    .limit(120);
  if (error) throw error;
  const ids = [...new Set((data ?? []).map((r) => r.content_id as string))].slice(0, 60);
  return withDeks(await contentByIds(ids), lang);
}

// ---------- Oy ve kitaplık ----------

export type MyState = { vote: number; saved: boolean };

export async function getMyStates(userId: string, ids: string[]): Promise<Map<string, MyState>> {
  const out = new Map<string, MyState>();
  if (ids.length === 0) return out;
  const [votes, lib] = await Promise.all([
    supabase.from('votes').select('content_id,value').eq('user_id', userId).in('content_id', ids),
    supabase.from('library_items').select('content_id,list').eq('user_id', userId).eq('list', 'saved').in('content_id', ids),
  ]);
  for (const id of ids) out.set(id, { vote: 0, saved: false });
  for (const v of votes.data ?? []) out.get(v.content_id as string)!.vote = v.value as number;
  for (const l of lib.data ?? []) out.get(l.content_id as string)!.saved = true;
  return out;
}

export async function setVote(userId: string, contentId: string, value: -1 | 0 | 1) {
  if (value === 0) {
    const { error } = await supabase.from('votes').delete().eq('user_id', userId).eq('content_id', contentId);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from('votes')
    .upsert({ user_id: userId, content_id: contentId, value }, { onConflict: 'user_id,content_id' });
  if (error) throw error;
}

export async function setSaved(userId: string, contentId: string, on: boolean) {
  const q = on
    ? supabase.from('library_items').upsert({ user_id: userId, content_id: contentId, list: 'saved' }, { onConflict: 'user_id,content_id,list' })
    : supabase.from('library_items').delete().eq('user_id', userId).eq('content_id', contentId).eq('list', 'saved');
  const { error } = await q;
  if (error) throw error;
}

export async function fetchSaved(userId: string, lang: string): Promise<Story[]> {
  const { data, error } = await supabase
    .from('library_items')
    .select('content_id')
    .eq('user_id', userId)
    .eq('list', 'saved')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return withDeks(await contentByIds((data ?? []).map((r) => r.content_id as string)), lang);
}

export async function getReadingLists(userId: string): Promise<ReadingList[]> {
  const [{ data: lists, error }, { data: items }] = await Promise.all([
    supabase.from('reading_lists').select('id,name,emoji').eq('user_id', userId).order('sort').order('created_at'),
    supabase.from('reading_list_items').select('list_id').eq('user_id', userId),
  ]);
  if (error) throw error;
  const counts = new Map<string, number>();
  for (const it of items ?? []) counts.set(it.list_id as string, (counts.get(it.list_id as string) ?? 0) + 1);
  return (lists ?? []).map((l) => ({ ...(l as Omit<ReadingList, 'item_count'>), item_count: counts.get(l.id as string) ?? 0 }));
}

export async function createReadingList(userId: string, name: string, emoji: string | null): Promise<string> {
  const { data, error } = await supabase
    .from('reading_lists')
    .insert({ user_id: userId, name: name.trim().slice(0, 60), emoji })
    .select('id')
    .single();
  if (error) throw error;
  return data.id as string;
}

export async function deleteReadingList(userId: string, listId: string) {
  const { error } = await supabase.from('reading_lists').delete().eq('user_id', userId).eq('id', listId);
  if (error) throw error;
}

export async function getListMembership(userId: string, contentId: string): Promise<Set<string>> {
  const { data } = await supabase.from('reading_list_items').select('list_id').eq('user_id', userId).eq('content_id', contentId);
  return new Set((data ?? []).map((r) => r.list_id as string));
}

export async function toggleListItem(userId: string, listId: string, contentId: string, on: boolean) {
  const q = on
    ? supabase.from('reading_list_items').upsert({ list_id: listId, content_id: contentId, user_id: userId }, { onConflict: 'list_id,content_id' })
    : supabase.from('reading_list_items').delete().eq('user_id', userId).eq('list_id', listId).eq('content_id', contentId);
  const { error } = await q;
  if (error) throw error;
}

export async function fetchListStories(userId: string, listId: string, lang: string): Promise<Story[]> {
  const { data, error } = await supabase
    .from('reading_list_items')
    .select('content_id')
    .eq('user_id', userId)
    .eq('list_id', listId)
    .order('added_at', { ascending: false });
  if (error) throw error;
  return withDeks(await contentByIds((data ?? []).map((r) => r.content_id as string)), lang);
}

// ---------- Kaynaklar ----------

export async function getFavoriteSourceIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase.from('source_favorites').select('source_id').eq('user_id', userId);
  if (error) throw error;
  return new Set((data ?? []).map((r) => r.source_id as string));
}

export async function setFavoriteSource(userId: string, sourceId: string, on: boolean) {
  const q = on
    ? supabase.from('source_favorites').upsert({ user_id: userId, source_id: sourceId }, { onConflict: 'user_id,source_id', ignoreDuplicates: true })
    : supabase.from('source_favorites').delete().eq('user_id', userId).eq('source_id', sourceId);
  const { error } = await q;
  if (error) throw error;
}

export async function getFollowedSourceIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase.from('user_sources').select('source_id').eq('user_id', userId);
  if (error) throw error;
  return new Set((data ?? []).map((r) => r.source_id as string));
}

export async function setFollowSource(userId: string, sourceId: string, on: boolean) {
  const q = on
    ? supabase.from('user_sources').upsert({ user_id: userId, source_id: sourceId }, { onConflict: 'user_id,source_id' })
    : supabase.from('user_sources').delete().eq('user_id', userId).eq('source_id', sourceId);
  const { error } = await q;
  if (error) throw error;
}

export async function followSources(userId: string, ids: string[]) {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from('user_sources')
    .upsert(ids.map((source_id) => ({ user_id: userId, source_id })), { onConflict: 'user_id,source_id' });
  if (error) throw error;
}

export type SourceCard = Pick<Source, 'id' | 'title' | 'kind' | 'site_url' | 'logo_url' | 'followers' | 'last_published_at'> & {
  category_slug: string | null;
};

export async function getSource(id: string): Promise<{ source: Source; itemCount: number; categorySlug: string | null } | null> {
  const { data, error } = await supabase.from('sources').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const [{ count }, { data: latest }, cats] = await Promise.all([
    supabase.from('content').select('id', { count: 'exact', head: true }).eq('source_id', id),
    supabase.from('content').select('category_id').eq('source_id', id).order('published_at', { ascending: false }).limit(12),
    getCategories(),
  ]);
  const freq = new Map<string, number>();
  for (const r of latest ?? []) if (r.category_id) freq.set(r.category_id as string, (freq.get(r.category_id as string) ?? 0) + 1);
  const top = [...freq.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  return { source: data as Source, itemCount: count ?? 0, categorySlug: cats.find((c) => c.id === top)?.slug ?? null };
}

export async function getUserSources(userId: string): Promise<SourceCard[]> {
  const { data, error } = await supabase
    .from('user_sources')
    .select('created_at, source:sources(id,title,kind,site_url,logo_url,followers,last_published_at)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? [])
    .map((r) => (r as unknown as { source: SourceCard | null }).source)
    .filter((s): s is SourceCard => !!s)
    .map((s) => ({ ...s, category_slug: null }));
}

/** İlgi alanlarında en çok yayın yapan, henüz takip edilmeyen kaynaklar. */
export async function recommendSources(userId: string, max = 12): Promise<SourceCard[]> {
  const { data: ints } = await supabase.from('user_interests').select('subcategory_id').eq('user_id', userId).gte('score', 6);
  const subIds = (ints ?? []).map((r) => r.subcategory_id as string);
  if (subIds.length === 0) return [];
  const { data: tags } = await supabase.from('content_tags').select('content_id').in('subcategory_id', subIds.slice(0, 60)).limit(600);
  const contentIds = [...new Set((tags ?? []).map((r) => r.content_id as string))];
  const freq = new Map<string, number>();
  for (let i = 0; i < contentIds.length; i += 150) {
    const { data } = await supabase.from('content').select('source_id').in('id', contentIds.slice(i, i + 150));
    for (const r of data ?? []) freq.set(r.source_id as string, (freq.get(r.source_id as string) ?? 0) + 1);
  }
  const followed = await getFollowedSourceIds(userId);
  const wanted = [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => id)
    .filter((id) => !followed.has(id))
    .slice(0, max);
  if (wanted.length === 0) return [];
  const { data: rows } = await supabase
    .from('sources')
    .select('id,title,kind,site_url,logo_url,followers,last_published_at')
    .in('id', wanted);
  const order = new Map(wanted.map((id, i) => [id, i]));
  return ((rows ?? []) as SourceCard[])
    .map((s) => ({ ...s, category_slug: null }))
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

export async function searchSources(q: string, limit = 6): Promise<SourceCard[]> {
  const term = q.trim().replace(/[%_,().\\*:]/g, ' ').trim();
  if (term.length < 2) return [];
  const { data } = await supabase
    .from('sources')
    .select('id,title,kind,site_url,logo_url,followers,last_published_at')
    .ilike('title', `%${term}%`)
    .limit(limit);
  return ((data ?? []) as SourceCard[]).map((s) => ({ ...s, category_slug: null }));
}

export type SourcePreview = { name: string | null; description: string | null; image: string | null; followers: string | null; host: string };

export async function previewSource(url: string): Promise<SourcePreview> {
  const { data, error } = await supabase.functions.invoke('source-preview', { body: { url } });
  if (error) throw await unwrapFunctionError(error);
  const r = data as SourcePreview & { error?: string };
  if (r.error) throw new Error(r.error);
  return r;
}

const SOCIAL_LIMIT = { free: 2, premium: 10 };

/** Bağlantıyı RSS'e çözümle, kaynağı oluştur (ya da var olanı bul) ve takip et. */
export async function addUserFeed(userId: string, url: string, premium: boolean): Promise<string> {
  const input = url.trim();
  // Sosyal (LinkedIn şirket) sınırı kota harcamadan önce; asıl kontrol sunucuda.
  if (/^https?:\/\/(?:www\.)?linkedin\.com\/company\//i.test(input)) {
    const { count } = await supabase
      .from('sources')
      .select('id', { count: 'exact', head: true })
      .eq('added_by', userId)
      .eq('kind', 'social_rss');
    if ((count ?? 0) >= (premium ? SOCIAL_LIMIT.premium : SOCIAL_LIMIT.free)) {
      if (!premium) throw new QuotaError('premium_required');
      throw new QuotaError('quota_exceeded');
    }
  }
  // Kaynak satırı sunucuda yazılır (resolve-feed subscribe): RLS istemciye
  // yalnız kişisel kaynak ekletir, kamusal kanallar ise global havuza girmeli
  // ki AI işleme maliyeti paylaşılsın. Takip de aynı çağrıda yapılır.
  const { data, error: re } = await supabase.functions.invoke('resolve-feed', {
    body: { url: input, subscribe: true },
  });
  if (re) throw await unwrapFunctionError(re);
  throwIfQuota(data);
  const resolved = data as { source_id?: string; error?: string };
  if (resolved.error || !resolved.source_id) throw new Error(resolved.error ?? 'feed');
  invalidateSourceMap();
  return resolved.source_id;
}

// ---------- İlgi alanları, onboarding, profil ----------

export async function getInterests(userId: string): Promise<Map<string, number>> {
  const { data, error } = await supabase.from('user_interests').select('subcategory_id,score').eq('user_id', userId);
  if (error) throw error;
  return new Map((data ?? []).map((r) => [r.subcategory_id as string, Number(r.score)]));
}

export async function saveInterestScores(userId: string, picks: { subcategoryId: string; score: number }[]) {
  if (picks.length === 0) return;
  const { error } = await supabase.from('user_interests').upsert(
    picks.map((p) => ({ user_id: userId, subcategory_id: p.subcategoryId, score: p.score, source: 'user_set', updated_at: new Date().toISOString() })),
    { onConflict: 'user_id,subcategory_id' }
  );
  if (error) throw error;
}

export async function updateProfile(userId: string, patch: Partial<Profile>) {
  const { error } = await supabase.from('profiles').update(patch).eq('id', userId);
  if (error) throw error;
}

export type PersonaResult = { persona: string; subcategoryIds: string[]; strongSubcategoryIds: string[] };

export async function generatePersona(linkedinUrl?: string | null): Promise<PersonaResult> {
  const body = linkedinUrl ? { linkedinUrl, linkedinText: '' } : undefined;
  const { data, error } = await supabase.functions.invoke('persona', body ? { body } : undefined);
  if (error) throw await unwrapFunctionError(error);
  const r = data as Partial<PersonaResult> & { error?: string };
  if (!r.persona) throw new Error(r.error ?? 'persona');
  return { persona: r.persona, subcategoryIds: r.subcategoryIds ?? [], strongSubcategoryIds: r.strongSubcategoryIds ?? [] };
}

export function extractLinkedInUrl(text: string | null | undefined): string | null {
  const m = (text ?? '').match(/(?:https?:\/\/)?(?:[a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9_%-]+\/?/i);
  if (!m) return null;
  return m[0].startsWith('http') ? m[0] : `https://${m[0]}`;
}

/** Onboarding sonu: ilgi skorları + profil; güçlü alanlar için ilk kaynaklar takip edilir. */
export async function saveOnboarding(
  userId: string,
  picks: { subcategoryId: string; score: number }[],
  budgetMin: number,
  digestTimes: string[],
  contentLang: string
) {
  await saveInterestScores(userId, picks);
  await updateProfile(userId, {
    daily_time_budget_min: budgetMin,
    digest_times: digestTimes,
    content_lang: contentLang,
  } as Partial<Profile>);
  await supabase.from('profiles').update({ default_fetch_interval: '6h' }).eq('id', userId);
  const strong = picks.filter((p) => p.score >= 7).map((p) => p.subcategoryId);
  if (strong.length > 0) {
    const recs = await recommendSources(userId, 12).catch(() => []);
    await followSources(userId, recs.map((r) => r.id)).catch(() => undefined);
  }
  if (contentLang !== 'en') {
    // Son 24 saati seçilen dile hazırla (sunucuda toplu; sessizce başarısız olabilir).
    void supabase.functions.invoke('translate-recent', { body: { lang: contentLang, hours: 24 } }).catch(() => undefined);
  }
}

export async function deleteAccount() {
  const { error } = await supabase.functions.invoke('delete-account');
  if (error) throw await unwrapFunctionError(error);
}

// ---------- Yorumlar ----------

export type CommentRow = { id: string; user_id: string; body: string; created_at: string; author_name: string | null };

export async function fetchComments(contentId: string, viewerId?: string): Promise<CommentRow[]> {
  const [{ data, error }, blocked] = await Promise.all([
    supabase
      .from('comments')
      .select('id,user_id,body,created_at')
      .eq('content_id', contentId)
      .eq('deleted', false)
      .order('created_at', { ascending: false })
      .limit(50),
    viewerId
      ? supabase.from('user_blocks').select('blocked_id').eq('blocker_id', viewerId).then((r) => new Set((r.data ?? []).map((x) => x.blocked_id as string)))
      : Promise.resolve(new Set<string>()),
  ]);
  if (error) throw error;
  const rows = ((data ?? []) as Omit<CommentRow, 'author_name'>[]).filter((r) => !blocked.has(r.user_id));
  const ids = [...new Set(rows.map((r) => r.user_id))];
  const names = new Map<string, string>();
  if (ids.length > 0) {
    const { data: profs } = await supabase.from('public_profiles').select('id,full_name').in('id', ids);
    for (const p of profs ?? []) if (p.full_name) names.set(p.id as string, p.full_name as string);
  }
  return rows.map((r) => ({ ...r, author_name: names.get(r.user_id) ?? null }));
}

export async function addComment(userId: string, contentId: string, body: string) {
  const { error } = await supabase.from('comments').insert({ user_id: userId, content_id: contentId, body: body.trim().slice(0, 4000) });
  if (error) throw error;
}

export async function deleteComment(userId: string, commentId: string) {
  const { error } = await supabase.from('comments').update({ deleted: true }).eq('id', commentId).eq('user_id', userId);
  if (error) throw error;
}

export async function reportComment(userId: string, commentId: string) {
  const { error } = await supabase.from('comment_reports').insert({ comment_id: commentId, reporter_id: userId });
  if (error && error.code !== '23505') throw error;
}

export async function blockUser(userId: string, blockedId: string) {
  const { error } = await supabase.from('user_blocks').upsert({ blocker_id: userId, blocked_id: blockedId }, { onConflict: 'blocker_id,blocked_id' });
  if (error) throw error;
}

export async function reportContent(userId: string, contentId: string) {
  const { error } = await supabase.from('content_reports').insert({ content_id: contentId, reporter_id: userId });
  if (error && error.code !== '23505') throw error;
}

// ---------- Hikâyeye sor ----------

export type ChatMsg = { role: 'user' | 'assistant'; text: string };

export async function fetchChat(userId: string, contentId: string): Promise<ChatMsg[]> {
  const { data } = await supabase
    .from('chat_messages')
    .select('role,text,created_at')
    .eq('user_id', userId)
    .eq('content_id', contentId)
    .order('created_at');
  return ((data ?? []) as ChatMsg[]).map((m) => ({ role: m.role, text: m.text }));
}

export async function sendChat(userId: string, contentId: string, history: ChatMsg[], text: string, lang: string): Promise<string> {
  const messages = [...history, { role: 'user' as const, text }].slice(-12).map((m) => ({ role: m.role, content: m.text }));
  const { data, error } = await supabase.functions.invoke('chat', { body: { contentId, messages, lang } });
  if (error) throw await unwrapFunctionError(error);
  throwIfQuota(data);
  const reply = (data as { reply?: string })?.reply;
  if (!reply) throw new Error('no reply');
  await supabase.from('chat_messages').insert([
    { user_id: userId, content_id: contentId, role: 'user', text },
    { user_id: userId, content_id: contentId, role: 'assistant', text: reply },
  ]);
  return reply;
}

export async function logListen(userId: string, contentId: string, durationSec: number) {
  await supabase.from('content_listens').insert({ user_id: userId, content_id: contentId, duration_sec: Math.round(durationSec) });
}

// ---------- Web aboneliği (Paddle) ----------

export type PaddleSub = {
  subscription_id: string;
  status: string;
  plan: string | null;
  price_id: string | null;
  current_period_end: string | null;
  scheduled_change: { action?: string; effective_at?: string } | null;
  canceled_at: string | null;
};

export async function getPaddleSubscription(userId: string): Promise<PaddleSub | null> {
  const { data } = await supabase
    .from('paddle_subscriptions')
    .select('subscription_id,status,plan,price_id,current_period_end,scheduled_change,canceled_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1);
  return (data?.[0] as PaddleSub | undefined) ?? null;
}

export type PortalLinks = { overview: string; cancel: string | null; update_payment: string | null };

export async function getPortalLinks(): Promise<PortalLinks> {
  const { data, error } = await supabase.functions.invoke('paddle-portal', { body: {} });
  if (error) throw await unwrapFunctionError(error);
  const r = data as PortalLinks & { error?: string };
  if (r.error) throw new Error(r.error);
  return r;
}

/** sync-premium: Adapty + Paddle'dan premium'u yeniden hesaplar. */
export async function syncPremium(): Promise<boolean> {
  const { data } = await supabase.functions.invoke('sync-premium', { body: {} });
  return !!(data as { premium?: boolean } | null)?.premium;
}
